import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { focusElement } from 'platform/utilities/ui';
import { querySelectorWithShadowRoot } from 'platform/utilities/ui/webComponents';
import { scrollTo } from 'platform/utilities/scroll';

// Single shared region so passkey alerts replace one another instead of stacking.
const PasskeyAlertContext = createContext(null);

export const usePasskeyAlert = () => useContext(PasskeyAlertContext);

// Moving focus to the headline on mount is the only way this alert is
// announced. Don't pair it with a hidden live region repeating the same text,
// or screen readers will read the heading more than once. Give each new alert
// a new `key` so it remounts and receives focus.
export function PasskeyAlert({ status, headline, children }) {
  const alertRef = useRef(null);

  useEffect(() => {
    const alertEl = alertRef.current;
    if (!alertEl) return undefined;
    let mounted = true;
    scrollTo(alertEl);
    // Wait for va-alert to hydrate so the slotted headline is focusable, and
    // skip focusing if this alert was replaced in the meantime so a stale
    // focus call can't pull focus off the alert that replaced it.
    querySelectorWithShadowRoot('h2[slot="headline"]', alertEl).then(
      headlineEl => {
        if (mounted && headlineEl?.isConnected) {
          focusElement(headlineEl, { preventScroll: true });
        }
      },
    );
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <va-alert
      ref={alertRef}
      status={status}
      visible
      class="vads-u-margin-bottom--2"
    >
      <h2 slot="headline">{headline}</h2>
      {children}
    </va-alert>
  );
}

PasskeyAlert.propTypes = {
  headline: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  children: PropTypes.node,
};

export default function PasskeyAlertProvider({ children }) {
  const [alert, setAlert] = useState(null);
  const value = useMemo(() => ({ setAlert }), []);
  return (
    <PasskeyAlertContext.Provider value={value}>
      {alert}
      {children}
    </PasskeyAlertContext.Provider>
  );
}

PasskeyAlertProvider.propTypes = {
  children: PropTypes.node,
};
